/**
 * AutoReplyStep — paso 3 del setup wizard.
 *
 * Configura respuestas automáticas:
 *  - Saludo: primer mensaje del cliente recibe respuesta auto inmediata
 *  - Fuera de horario: mensajes recibidos fuera de business hours
 *
 * En producción esto lo procesa el bot/AI; acá guardamos la config solamente.
 */

import { useState } from "react";
import { Loader2, Save, MessageSquare, Clock } from "lucide-react";
import { Button } from "../../../../shared/components/ui/Button";
import { whatsappAccountsApi } from "../../api";
import type { WhatsAppAccount, WhatsAppAutoReply } from "../../types";

interface Props {
    account: WhatsAppAccount;
    onComplete: (updated: WhatsAppAccount) => void;
}

const DEFAULT_GREETING = "¡Hola! Soy el asistente de tu marca 🤖 Te respondo a la brevedad.";
const DEFAULT_AWAY = "Gracias por escribirnos. En este momento estamos cerrados. Volvemos a responderte mañana en horario de atención.";

export default function AutoReplyStep({ account, onComplete }: Props) {
    const existing = account.autoReply;
    const [config, setConfig] = useState<WhatsAppAutoReply>({
        greetingEnabled: existing?.greetingEnabled ?? true,
        greetingMessage: existing?.greetingMessage || DEFAULT_GREETING,
        awayEnabled: existing?.awayEnabled ?? true,
        awayMessage: existing?.awayMessage || DEFAULT_AWAY,
        businessHoursStart: existing?.businessHoursStart || "10:00",
        businessHoursEnd: existing?.businessHoursEnd || "21:00",
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        const res = await whatsappAccountsApi.saveAutoReply(account.id, config);
        setSaving(false);
        if (!res.success) {
            setError(res.error || "Error al guardar");
            return;
        }
        onComplete(res.data);
    };

    return (
        <div className="space-y-5">
            <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-violet-50 text-violet-600">
                    <MessageSquare size={16} />
                </div>
                <div>
                    <h3 className="text-base font-bold text-slate-900">
                        Auto-respuestas
                    </h3>
                    <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                        Respuestas automáticas para cuando un cliente escribe sin que vos
                        estés mirando.
                    </p>
                </div>
            </div>

            {/* Saludo */}
            <div className="bg-white border border-slate-200 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <h4 className="text-sm font-bold text-slate-900">
                            Saludo automático
                        </h4>
                        <p className="text-[11px] text-slate-500 mt-0.5">
                            Se envía cuando un cliente te escribe por primera vez.
                        </p>
                    </div>
                    <Toggle
                        value={config.greetingEnabled}
                        onChange={(v) =>
                            setConfig((c) => ({ ...c, greetingEnabled: v }))
                        }
                    />
                </div>
                {config.greetingEnabled && (
                    <textarea
                        value={config.greetingMessage || ""}
                        onChange={(e) =>
                            setConfig((c) => ({
                                ...c,
                                greetingMessage: e.target.value,
                            }))
                        }
                        rows={3}
                        className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:border-green-300 focus:ring-1 focus:ring-green-100 resize-none"
                    />
                )}
            </div>

            {/* Fuera de horario */}
            <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                    <div>
                        <h4 className="text-sm font-bold text-slate-900">
                            Fuera de horario
                        </h4>
                        <p className="text-[11px] text-slate-500 mt-0.5">
                            Cuando llegan mensajes fuera del horario de atención.
                        </p>
                    </div>
                    <Toggle
                        value={config.awayEnabled}
                        onChange={(v) => setConfig((c) => ({ ...c, awayEnabled: v }))}
                    />
                </div>

                {config.awayEnabled && (
                    <>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">
                                    Apertura
                                </label>
                                <input
                                    type="time"
                                    value={config.businessHoursStart || "10:00"}
                                    onChange={(e) =>
                                        setConfig((c) => ({
                                            ...c,
                                            businessHoursStart: e.target.value,
                                        }))
                                    }
                                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 font-mono focus:outline-none focus:border-green-300"
                                />
                            </div>
                            <div>
                                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">
                                    Cierre
                                </label>
                                <input
                                    type="time"
                                    value={config.businessHoursEnd || "21:00"}
                                    onChange={(e) =>
                                        setConfig((c) => ({
                                            ...c,
                                            businessHoursEnd: e.target.value,
                                        }))
                                    }
                                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 font-mono focus:outline-none focus:border-green-300"
                                />
                            </div>
                        </div>
                        <textarea
                            value={config.awayMessage || ""}
                            onChange={(e) =>
                                setConfig((c) => ({
                                    ...c,
                                    awayMessage: e.target.value,
                                }))
                            }
                            rows={3}
                            className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:border-green-300 focus:ring-1 focus:ring-green-100 resize-none"
                        />
                        <p className="text-[10px] text-slate-400 flex items-center gap-1">
                            <Clock size={10} />
                            Se envía entre las{" "}
                            <span className="font-mono font-semibold">
                                {config.businessHoursEnd}
                            </span>{" "}
                            y las{" "}
                            <span className="font-mono font-semibold">
                                {config.businessHoursStart}
                            </span>
                            .
                        </p>
                    </>
                )}
            </div>

            {error && (
                <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg p-2.5">
                    {error}
                </div>
            )}

            <div className="flex justify-end">
                <Button variant="primary" onClick={handleSave} disabled={saving}>
                    {saving ? (
                        <Loader2 size={14} className="animate-spin" />
                    ) : (
                        <Save size={14} />
                    )}
                    Guardar y continuar
                </Button>
            </div>
        </div>
    );
}

function Toggle({
    value,
    onChange,
}: {
    value: boolean;
    onChange: (v: boolean) => void;
}) {
    return (
        <button
            type="button"
            role="switch"
            aria-checked={value}
            onClick={() => onChange(!value)}
            className={`relative w-10 h-5 rounded-full transition-colors ${
                value ? "bg-green-500" : "bg-slate-300"
            }`}
        >
            <span
                className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all ${
                    value ? "left-5" : "left-0.5"
                }`}
            />
        </button>
    );
}
